import { useLocationsListing } from "app/features/locations/hooks/useLocationsListing";
import { FieldValues, ControllerProps, Controller } from "react-hook-form";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { Text, View } from "react-native";
import { FormControl } from "ui/gluestack/form-control";
import { FormControlledMultiSelectDropdown } from "ui/molecules/form/FormControlledMultiSelectDropdown";
import { useEffect } from "react";

interface Props<TFieldValues extends FieldValues = FieldValues> {
  control: ControllerProps<TFieldValues>["control"];
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  className?: string;
  hideSearch?: boolean;
  canClear?: boolean;
  multiselect?: boolean;
}

function SingleLocationContent({
  location,
  multiselect,
  onChange,
}: {
  location: { label: string; value: number };
  multiselect?: boolean;
  onChange: (value: number | number[]) => void;
}) {
  useEffect(() => {
    onChange(multiselect ? [location.value] : location.value);
  }, [location, multiselect, onChange]);

  return (
    <View className="border border-background-300 bg-background-100 rounded h-10 flex px-2 justify-center">
      <Text>{location.label}</Text>
    </View>
  );
}

export default function FormControlledLocationSelector<
  TFieldValues extends FieldValues = FieldValues,
>({
  control,
  name,
  label,
  className,
  hideSearch,
  canClear,
  multiselect,
}: Props<TFieldValues>) {
  const { isLoading, locationOptions } = useLocationsListing();

  if (locationOptions.length === 1) {
    return (
      <FormControl>
        {label && (
          <Text className="text-sm font-medium mb-2 text-gray-600">
            {label}
          </Text>
        )}
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange } }) => (
            <SingleLocationContent
              location={locationOptions[0]!}
              onChange={onChange}
              multiselect={multiselect}
            />
          )}
        />
      </FormControl>
    );
  }

  if (multiselect) {
    return (
      <FormControlledMultiSelectDropdown
        name={name}
        control={control}
        label={label}
        placeholder="Select location(s)"
        isRequired
        isDisabled={isLoading}
        options={locationOptions}
      />
    );
  }

  return (
    <FormControlledDropdown
      label={label}
      name={name}
      control={control}
      placeholder="Choose location"
      isDisabled={isLoading}
      options={locationOptions}
      className={className}
      hideSearch={hideSearch}
      canClear={canClear}
    />
  );
}
